import { useEffect, useEffectEvent, useRef } from 'react';

type UseEffectOnChangeOptions<T> = {
  /** When set, a change is detected only when this returns true (e.g. deep field compare). */
  isChange?: (prev: T, next: T) => boolean;
};

/**
 * Runs `onChange(next, prev)` after commit when `value` changes from the last run.
 * Does not run on first mount
 */
export const useEffectOnChange = <T>(
  value: T,
  onChange: (next: T, prev: T) => void,
  options?: UseEffectOnChangeOptions<T>
): void => {
  const prevRef = useRef(value);
  const isChange = options?.isChange;

  const callback = useEffectEvent((v: T) => {
    const prev = prevRef.current;
    const changed = isChange ? isChange(prev, v) : !Object.is(prev, v);
    if (!changed) {
      return;
    }
    prevRef.current = v;
    onChange(v, prev);
  });

  useEffect(() => {
    callback(value);
  }, [value]);
};
